import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Send, Transform } from '@mui/icons-material';
import { updateDataSetInfo } from 'js/actions';
import ResourceDetailHeader from 'js/components/ResourceDetailHeader/ResourceDetailHeader';
import DataSetDetailActionsContainer from 'js/components/data_sets/cards/DataSetDetailActionsContainer';
import ViewEntityPipelineButtonContainer from 'js/components/pipelines/cards/ViewEntityPipelineButtonContainer';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import { SearchFilterTypes } from 'js/constants/visibilityFilters';
import { Button } from 'new/elements/Button';
import { Stack } from 'new/elements/Stack';
import { User } from 'new/entities/User';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import PipelineStatusBadge, {
  Status,
} from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import Typography from 'new/elements/Typography';
import useRagFlowId from 'new/components/flows/types/genai_rag/context/RagFlowId/useRagFlowId';

import styles from './DataSetDetailHeader.module.scss';

interface DataSetDetailHeaderProps {
  readOnly: boolean;
  adminAccess: boolean;
  owner: User;
  data: any;
  hideActions: boolean;
  shared?: boolean;
  isEditable: any;
  isDetected: boolean;
  disableEditMessage: any;
  isManaged: any;
  onCloseDrawer?: () => void;
  connectionOrVendorType: any;
  statsContentView: any;
  showPipelineButton?: boolean;
  showTransform?: boolean;
  showSend?: boolean;
  disableActions?: boolean;
}

const Connection = ResourceDetailHeader.TopContent.Connection;
const ViewFlowButton = ResourceDetailHeader.TopContent.ViewFlowButton;

export const DataSetDetailHeader = (props: DataSetDetailHeaderProps) => {
  const {
    readOnly,
    adminAccess,
    data,
    hideActions,
    isEditable,
    isDetected,
    disableEditMessage,
    isManaged,
    showPipelineButton = true,
    showTransform,
    showSend,
    statsContentView,
    connectionOrVendorType,
    disableActions,
  } = props;
  const dispatch = useAppDispatch();

  const isRagFlowContext = useRagFlowId();
  const publicDatasets = useSelector((state: any) => state.publicDatasets);
  const sharedDatasets = useSelector((state: any) => state.sharedDatasets);

  const shared = useMemo(
    () => publicDatasets.byId.includes(data.id) || sharedDatasets.byId.includes(data.id),
    [data.id, publicDatasets, sharedDatasets],
  );

  const updateInfo = (id, info) => {
    dispatch(updateDataSetInfo(data.id, info));
  };

  const canShowTransform = (() => {
    if (showTransform !== undefined) return showTransform;
    return true;
  })();

  const canShowSend = (() => {
    if (shared) return false;
    if (showSend !== undefined) return showSend;
    return true;
  })();

  const nodeName = (() => {
    if (data.node_type === 'query_rewriter') return 'Query Rewriter';
    if (data.node_type === 'context_enricher') return 'Context Enricher';
    if (data.node_type === 'reranker') return 'Reranker';
    if (data.node_type === 'api_request') return 'Request';
    if (data.node_type === 'api_success_modifier') return 'Transform';
    if (data.node_type === 'splitter_output') return 'Splitter output';
    if (data.node_type === 'api_result') return 'Result';
    if (data.node_type === 'api_error') return 'Error';
    return undefined;
  })();

  const shouldHideActions = hideActions || !!isRagFlowContext; // if we're in the context of a rag flow, don't show actions

  return (
    <ResourceDetailHeader
      readOnly={readOnly || data.node_type === 'splitter_output'}
      adminAccess={adminAccess}
      data={data}
      updateInfo={updateInfo}
      onCloseDrawer={props?.onCloseDrawer}
    >
      <ResourceDetailHeader.TopContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap="16px">
          <Stack direction="row" alignItems="center" gap="16px">
            {nodeName ? (
              <ResourceDetailHeader.TopContent.ConnectionBox>
                <Typography>{nodeName}</Typography>
              </ResourceDetailHeader.TopContent.ConnectionBox>
            ) : (
              <Connection
                resourceType={RESOURCE_TYPE_DATA_SET}
                data={{ ...data, ...props, isDetected, shared }}
              />
            )}
            <ChipWithCopy tooltipTitle="Copy Nexset ID" toCopy={data?.id}>
              ID {data?.id}
            </ChipWithCopy>
            <PipelineStatusBadge
              id={data.id}
              status={data.status as Status}
              runtimeStatus={data?.runtime_status}
              ingestionMode={data.ingestion_mode}
              parentArchivalStatus={data?.archival_status}
            />
          </Stack>
          <Stack direction="row" alignItems="center" gap="16px">
            {!disableActions && (
              <>
                {showPipelineButton && !shared && (
                  <ViewEntityPipelineButtonContainer
                    filterType={SearchFilterTypes.NEXSET_ID}
                    filterValue={data.id}
                    className={'view-entity-pipeline-button-position'}
                    ButtonComponent={<ViewFlowButton />}
                  />
                )}

                {shouldHideActions ? null : (
                  <DataSetDetailActionsContainer
                    data={data}
                    TransformComponent={
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        style={{ backgroundColor: 'white' }}
                        className={styles.transformButton}
                        endIcon={<Transform />}
                      >
                        Transform
                      </Button>
                    }
                    SendComponent={
                      <Button
                        size="small"
                        variant="outlined"
                        color="primary"
                        style={{ backgroundColor: 'white' }}
                        className={styles.sendButton}
                        endIcon={<Send />}
                      >
                        Send to Destination
                      </Button>
                    }
                    showMenu={!shared}
                    showTransform={canShowTransform}
                    showSend={canShowSend}
                    isDetected={isDetected}
                    showStatus={false}
                    disableEditing={!isEditable}
                    disableEditMessage={disableEditMessage}
                    readOnly={readOnly}
                    isManaged={isManaged}
                    connectionOrVendorType={connectionOrVendorType}
                    hideMenuDetails={true}
                  />
                )}
              </>
            )}
          </Stack>
        </Stack>
      </ResourceDetailHeader.TopContent>
      {statsContentView}
    </ResourceDetailHeader>
  );
};
